import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "@corti/dictation-web";

import {
  Loader2,
  Mic,
  Square,
  Calendar,
  Clock,
  ChevronDown,
  FileText,
  Plus,
} from "lucide-react";

import { useTranslation } from "react-i18next";
import Cookies from "js-cookie";

import useConsultations from "../../../hooks/useConsultations";

import { axiosPrescription, axiosMedical, axiosExtraction } from "../../../../api/axios";

import AddMetricModal from "@/Profile/components/OverViewsPage/AddMetricModal/AddMetricModal";

import "./AddNewConsultationZone.css";

/* =========================================================
   DEFAULT ZONES
========================================================= */

const buildDefaultZones = (t) => [
  {
    id: "history",

    title: t("addConsultation.zones.history.title", {
      defaultValue: "Patient History + Clinical Examination",
    }),

    placeholder: t("addConsultation.zones.history.placeholder", {
      defaultValue:
        "Describe the patient's history and clinical examination...",
    }),

    example: "",

    value: "",
  },

  {
    id: "diagnostic",

    title: t("addConsultation.zones.diagnostic.title", {
      defaultValue: "Diagnosis + Management Plan",
    }),

    placeholder: t("addConsultation.zones.diagnostic.placeholder", {
      defaultValue:
        "Describe the diagnosis and management plan...",
    }),

    example: t("addConsultation.zones.diagnostic.example", {
      defaultValue: "",
    }),

    value: "",
  },
];

/* =========================================================
   VITALS
========================================================= */

const VITAL_FIELDS = [
  {
    key: "glycemie",
    labelKey: "medicalOverview.metrics.bloodGlucose",
    defaultLabel: "Glycémie",
    type: "GLYCEMIA",
    unit: "mg/dL",
  },

  {
    key: "bloodPressure",
    labelKey: "medicalOverview.metrics.bloodPressure",
    defaultLabel: "Tension artérielle",
    isBloodPressure: true,
    unit: "mmHg",
  },

  {
    key: "pouls",
    labelKey: "medicalOverview.metrics.heartRate",
    defaultLabel: "Fréquence cardiaque",
    type: "HEART_RATE",
    unit: "bpm",
  },

  {
    key: "respRate",
    labelKey: "medicalOverview.metrics.respiratoryRate",
    defaultLabel: "Fréquence respiratoire",
    type: "RESP_RATE",
    unit: "rpm",
  },


  {
    key: "temp",
    labelKey: "medicalOverview.metrics.temperature",
    defaultLabel: "Température",
    type: "TEMPERATURE",
    unit: "°C",
  },

  {
    key: "spo2",
    labelKey: "medicalOverview.metrics.oxygenSaturation",
    defaultLabel: "SpO₂",
    type: "OXYGEN_SAT",
    unit: "%",
  },
];

const EMPTY_VITALS = {
  glycemie: "",
  bloodPressure: "",
  pouls: "",
  respRate: "",
  temp: "",
  spo2: "",
};

/* =========================================================
   UTILS
========================================================= */

const uid = () =>
  `zone_${Math.random()
    .toString(36)
    .slice(2, 10)}_${Date.now()}`;

const clampAudioLevel = (value) => {
  const numberValue = Number(value || 0);

  if (!Number.isFinite(numberValue)) {
    return 0;
  }

  return Math.max(
    0,
    Math.min(
      1,
      numberValue > 1
        ? numberValue / 100
        : numberValue
    )
  );
};

const getTranscriptData = (event) => {
  const detail = event?.detail || {};

  return detail?.data || detail;
};

const normalizeArray = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value;
  }

  if (Array.isArray(value.data)) {
    return value.data;
  }

  if (Array.isArray(value.items)) {
    return value.items;
  }

  if (Array.isArray(value.results)) {
    return value.results;
  }

  if (Array.isArray(value.history)) {
    return value.history;
  }

  if (Array.isArray(value.records)) {
    return value.records;
  }

  return [];
};


const parseNumericValue = (value) => {
  if (
    value === undefined ||
    value === null ||
    String(value).trim() === ""
  ) {
    return null;
  }

  const number = Number(
    String(value)
      .trim()
      .replace(",", ".")
  );

  return Number.isFinite(number)
    ? number
    : null;
};

const getMeasurementTimestamp = (item) => {
  if (!item) return 0;

  const candidate =
    item.measuredAt ||
    item.updatedAt ||
    item.createdAt ||
    item.addedAt ||
    item.recordedAt ||
    item.date ||
    item.timestamp ||
    0;

  const timestamp =
    new Date(candidate).getTime();

  return Number.isNaN(timestamp)
    ? 0
    : timestamp;
};

const getLatestVitalByType = (
  list,
  type
) => {
  if (!Array.isArray(list)) {
    return null;
  }

  const matches = list.filter(
    (item) =>
      String(item?.type || "").toUpperCase() ===
      String(type || "").toUpperCase()
  );

  if (!matches.length) {
    return null;
  }

  return [...matches].sort(
    (a, b) =>
      getMeasurementTimestamp(b) -
      getMeasurementTimestamp(a)
  )[0];
};


const valuesAreEqual = (
  first,
  second
) => {
  const firstNumber =
    parseNumericValue(first);

  const secondNumber =
    parseNumericValue(second);

  if (
    firstNumber === null &&
    secondNumber === null
  ) {
    return true;
  }

  if (
    firstNumber === null ||
    secondNumber === null
  ) {
    return false;
  }

  return firstNumber === secondNumber;
};

const makeCustomVitalKey = (label, unit) =>
  `${String(label || "Mesure").trim().toLowerCase()}__${String(
    unit || ""
  )
    .trim()
    .toLowerCase()}`;

const buildLatestCustomVitals = (vitalList) => {
  if (!Array.isArray(vitalList)) {
    return [];
  }

  const groups = new Map();

  vitalList
    .filter((item) => String(item?.type || "").toUpperCase() === "OTHER")
    .forEach((item) => {
      const label = String(item?.customLabel || "Mesure").trim();
      const unit = String(item?.unit || "").trim();
      const key = makeCustomVitalKey(label, unit);
      const current = groups.get(key);

      if (!current || getMeasurementTimestamp(item) > getMeasurementTimestamp(current)) {
        groups.set(key, item);
      }
    });

  return Array.from(groups.entries()).map(([key, item]) => ({
    key,
    backendId: item?.id,
    label: String(item?.customLabel || "Mesure").trim(),
    unit: String(item?.unit || "").trim(),
    value: item?.value != null ? String(item.value) : "",
    measuredAt: item?.measuredAt || item?.createdAt || null,
    isNew: false,
  }));
};

/*
 * Sur la page médecin on force le JWT médecin si présent.
 * Cela évite qu'un ancien cookie patient soit choisi en priorité.
 */
const getDoctorMedicalRequestConfig = () => {
  const doctorToken =
    Cookies.get("doctorAccessToken") ||
    Cookies.get("doctor_token") ||
    Cookies.get("doctorToken") ||
    localStorage.getItem("doctorAccessToken") ||
    null;

  if (!doctorToken) {
    return {};
  }

  return {
    headers: {
      Authorization: `Bearer ${doctorToken}`,
    },
  };
};

const parseBloodPressure = (value) => {
  const clean = String(value || "").trim().replace(/\s+/g, "");

  if (!clean) {
    return { sys: null, dia: null };
  }

  const parts = clean.split("/");

  if (parts.length !== 2) {
    return { sys: null, dia: null };
  }

  return {
    sys: parseNumericValue(parts[0]),
    dia: parseNumericValue(parts[1]),
  };
};

/* =========================================================
   CURRENT DATE / TIME
========================================================= */

const getTodayLocal = () => {
  const now = new Date();

  const year = now.getFullYear();

  const month = String(
    now.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    now.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getCurrentTimeLocal = () => {
  const now = new Date();

  const hours = String(
    now.getHours()
  ).padStart(2, "0");

  const minutes = String(
    now.getMinutes()
  ).padStart(2, "0");

  return `${hours}:${minutes}`;
};

/* =========================================================
   DOCUMENT DRAFT

   Used to pass context to the prescription step.
========================================================= */

const EMPTY_DOCUMENT_TEXTS = {
  prescription: "",
  bilan: "",
  radio: "",
  lettre: "",
};

/* =========================================================
   CONSULTATION RESULT HELPERS
========================================================= */

const extractConsultationId = (result) => {
  if (!result) return null;

  if (typeof result === "string") {
    return result.trim() || null;
  }

  const payload = result?.payload;

  if (payload) {
    if (typeof payload === "string") {
      return payload.trim() || null;
    }

    if (payload?.id) return String(payload.id).trim();
    if (payload?._id) return String(payload._id).trim();
    if (payload?.data?.id) return String(payload.data.id).trim();
    if (payload?.data?._id) return String(payload.data._id).trim();
  }

  if (result?.id) return String(result.id).trim();
  if (result?._id) return String(result._id).trim();
  if (result?.data?.id) return String(result.data.id).trim();
  if (result?.data?._id) return String(result.data._id).trim();

  return null;
};

const getConsultationErrorMessage = (resultOrError) => {
  const value = resultOrError || {};

  const payloadString =
    typeof value?.payload === "string"
      ? value.payload
      : null;

  const message =
    value?.payload?.message ??
    value?.payload?.error?.message ??
    payloadString ??
    value?.response?.data?.message ??
    value?.data?.message ??
    value?.error?.message ??
    value?.message ??
    "Impossible d'enregistrer la consultation.";

  if (Array.isArray(message)) {
    return message.join(", ");
  }

  if (typeof message === "object") {
    try {
      return JSON.stringify(message);
    } catch {
      return "Impossible d'enregistrer la consultation.";
    }
  }

  return String(message || "Impossible d'enregistrer la consultation.");
};

/* =========================================================
   DATE FORMAT
========================================================= */

const formatDatePretty = (isoDate) => {
  if (!isoDate) {
    return "";
  }

  const date = new Date(
    `${isoDate}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* =========================================================
   TIME FORMAT
========================================================= */

const formatTimePretty = (time24) => {
  if (!time24) {
    return "";
  }

  const [hours, minutes] = time24
    .split(":")
    .map(Number);

  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes)
  ) {
    return time24;
  }

  const date = new Date();

  date.setHours(
    hours,
    minutes,
    0,
    0
  );

  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* =========================================================
   COMPONENT
========================================================= */

export default function AddNewConsultationZone({
  userId,
  doctorId,

  onCancel,
  onSuccess,

  onOpenPrescribePopup,

  onTotalChange,

  recentDocuments = [],

  onViewAllDocuments,
  onAddDocument,
  onDownloadDocument,
  onHideDocument,
}) {
  const { t, i18n } = useTranslation();

  const isRTL = (
    i18n.language || ""
  ).startsWith("ar");

  /* =======================================================
     CONSULTATION API
  ======================================================= */

  const {
    create: createConsultation,
    isLoading: isSavingConsultation,
  } = useConsultations();

  const [
    consultationSaveError,
    setConsultationSaveError,
  ] = useState("");

  const [loadedRecentDocuments, setLoadedRecentDocuments] = useState([]);

  useEffect(() => {
    const cleanUserId = String(userId || "").trim();
    if (!cleanUserId) {
      setLoadedRecentDocuments([]);
      return undefined;
    }

    let mounted = true;

    axiosExtraction
      .get("/my-documents", {
        params: { skip: 0, limit: 1, user_id: cleanUserId },
      })
      .then((response) => {
        if (!mounted) return;
        const payload = response?.data;
        const documents = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.documents)
          ? payload.documents
          : Array.isArray(payload?.data?.documents)
          ? payload.data.documents
          : Array.isArray(payload?.data)
          ? payload.data
          : [];

        setLoadedRecentDocuments(
          [...documents]
            .sort(
              (first, second) =>
                new Date(second.date_upload || second.createdAt || 0) -
                new Date(first.date_upload || first.createdAt || 0)
            )
            .slice(0, 1)
            .map((document) => ({
              id: document._id || document.id,
              name: document.nom_fichier || document.type_document || "Medical document",
              date: document.date_upload || document.createdAt || null,
              status: document.status || "",
              fileUrl: document.chemin_fichier || document.file_path || document.url || "",
            }))
        );
      })
      .catch(() => {
        if (mounted) setLoadedRecentDocuments([]);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const documentsForPreview =
    loadedRecentDocuments.length > 0 ? loadedRecentDocuments : recentDocuments;

  /* =======================================================
     ZONES
  ======================================================= */

  const [zones, setZones] = useState(() =>
    buildDefaultZones(t)
  );

  const [
    openZones,
    setOpenZones,
  ] = useState(() => ({
    history: true,
    diagnostic: true,
  }));

  /* =======================================================
     CONSULTATION REASON
  ======================================================= */

  const [
    consultationReason,
    setConsultationReason,
  ] = useState("");

  /* =======================================================
     DATE / TIME
  ======================================================= */

  const [
    consultationDate,
    setConsultationDate,
  ] = useState(getTodayLocal);

  const [
    consultationTime,
    setConsultationTime,
  ] = useState(getCurrentTimeLocal);

  const [
    isEditingDateTime,
    setIsEditingDateTime,
  ] = useState(false);

  /* =======================================================
     VITALS
  ======================================================= */

  const [vitals, setVitals] = useState({
    ...EMPTY_VITALS,
  });

  const initialVitalsRef = useRef({
    ...EMPTY_VITALS,
  });

  const [
    loadingVitals,
    setLoadingVitals,
  ] = useState(false);

  const [
    savingVitals,
    setSavingVitals,
  ] = useState(false);

  const [
    vitalsError,
    setVitalsError,
  ] = useState("");

  const [
    isEditingVitals,
    setIsEditingVitals,
  ] = useState(false);

  const [
    vitalsSuccess,
    setVitalsSuccess,
  ] = useState("");

  const [
    customVitals,
    setCustomVitals,
  ] = useState([]);

  const initialCustomVitalsRef = useRef([]);

  const [
    addVitalModalOpen,
    setAddVitalModalOpen,
  ] = useState(false);

  const hasAnyVital = useMemo(
    () =>
      VITAL_FIELDS.some(
        (field) =>
          String(vitals[field.key] ?? "").trim() !== ""
      ) || customVitals.length > 0,
    [vitals, customVitals]
  );

  const hasVitalChanges = useMemo(
    () =>
      VITAL_FIELDS.some((field) => {
        const current = vitals[field.key];

        if (
          current === undefined ||
          current === null ||
          String(current).trim() === ""
        ) {
          return false;
        }

        return !valuesAreEqual(
          initialVitalsRef.current[field.key],
          current
        );
      }),
    [vitals]
  );

  const hasUnsavedVitalChanges = useMemo(() => {
    if (hasVitalChanges) return true;

    const initialCustomByKey = new Map(
      initialCustomVitalsRef.current.map((item) => [item.key, item])
    );

    return customVitals.some((metric) => {
      const numericValue = parseNumericValue(metric.value);
      if (numericValue === null || !String(metric.label || "").trim()) {
        return false;
      }

      const initialMetric = initialCustomByKey.get(metric.key);
      return (
        !initialMetric ||
        !valuesAreEqual(initialMetric.value, metric.value) ||
        String(initialMetric.unit || "").trim() !== String(metric.unit || "").trim() ||
        String(initialMetric.label || "").trim() !== String(metric.label || "").trim()
      );
    });
  }, [customVitals, hasVitalChanges]);

  /*
   * Charge les dernières mesures du patient
   * dès que le médecin ouvre la consultation.
   */
  useEffect(() => {
    const cleanUserId =
      String(userId || "").trim();

    if (!cleanUserId) {
      setVitals({
        ...EMPTY_VITALS,
      });

      initialVitalsRef.current = {
        ...EMPTY_VITALS,
      };

      setCustomVitals([]);
      initialCustomVitalsRef.current = [];

      return;
    }

    let mounted = true;

    const loadPatientVitals = async () => {
      setLoadingVitals(true);
      setVitalsError("");

      const config =
        getDoctorMedicalRequestConfig();

      try {
        const results =
          await Promise.allSettled([
            axiosMedical.get(
              `/${cleanUserId}/vitals`,
              config
            ),
          ]);

        if (!mounted) {
          return;
        }

        const vitalResponse =
          results[0].status === "fulfilled"
            ? results[0].value?.data
            : [];

        const vitalList =
          normalizeArray(vitalResponse);

        const glycemia =
          getLatestVitalByType(
            vitalList,
            "GLYCEMIA"
          ) ||
          getLatestVitalByType(
            vitalList,
            "BLOOD_GLUCOSE"
          );

        const bpSys =
          getLatestVitalByType(
            vitalList,
            "BP_SYS"
          );

        const bpDia =
          getLatestVitalByType(
            vitalList,
            "BP_DIA"
          );

        const heartRate =
          getLatestVitalByType(
            vitalList,
            "HEART_RATE"
          );

        const respiratoryRate =
          getLatestVitalByType(
            vitalList,
            "RESP_RATE"
          ) ||
          getLatestVitalByType(
            vitalList,
            "RESPIRATORY_RATE"
          );

        const temperature =
          getLatestVitalByType(
            vitalList,
            "TEMPERATURE"
          );

        const oxygenSat =
          getLatestVitalByType(
            vitalList,
            "OXYGEN_SAT"
          );


        const loadedVitals = {
          glycemie:
            glycemia?.value != null
              ? String(glycemia.value)
              : "",

          bloodPressure:
            bpSys?.value != null && bpDia?.value != null
              ? `${bpSys.value}/${bpDia.value}`
              : bpSys?.value != null
              ? String(bpSys.value)
              : bpDia?.value != null
              ? String(bpDia.value)
              : "",

          pouls:
            heartRate?.value != null
              ? String(heartRate.value)
              : "",

          respRate:
            respiratoryRate?.value != null
              ? String(respiratoryRate.value)
              : "",


          temp:
            temperature?.value != null
              ? String(temperature.value)
              : "",

          spo2:
            oxygenSat?.value != null
              ? String(oxygenSat.value)
              : "",
        };

        setVitals(loadedVitals);

        initialVitalsRef.current =
          loadedVitals;

        const loadedCustomVitals =
          buildLatestCustomVitals(vitalList);

        setCustomVitals(loadedCustomVitals);
        initialCustomVitalsRef.current =
          loadedCustomVitals.map((item) => ({ ...item }));

        if (
          results.every(
            (result) =>
              result.status === "rejected"
          )
        ) {
          setVitalsError(
            "Impossible de charger les mesures du patient."
          );
        }

        console.log(
          "✅ Doctor consultation - patient vitals:",
          loadedVitals
        );
      } catch (error) {
        if (!mounted) {
          return;
        }

        console.error(
          "❌ Error loading patient vitals:",
          error?.response?.data ||
            error
        );

        setVitalsError(
          "Impossible de charger les mesures du patient."
        );
      } finally {
        if (mounted) {
          setLoadingVitals(false);
        }
      }
    };

    loadPatientVitals();

    return () => {
      mounted = false;
    };
  }, [userId]);

  const saveChangedPatientVitals =
    useCallback(async () => {
      const cleanUserId = String(userId || "").trim();

      if (!cleanUserId) {
        return false;
      }

      const changedFields = VITAL_FIELDS.filter((field) => {
        const currentValue = vitals[field.key];

        if (
          currentValue === undefined ||
          currentValue === null ||
          String(currentValue).trim() === ""
        ) {
          return false;
        }

        if (field.isBloodPressure) {
          return (
            String(initialVitalsRef.current[field.key] || "")
              .replace(/\s+/g, "") !==
            String(currentValue || "")
              .replace(/\s+/g, "")
          );
        }

        return !valuesAreEqual(
          initialVitalsRef.current[field.key],
          currentValue
        );
      });

      const initialCustomByKey = new Map(
        initialCustomVitalsRef.current.map((item) => [item.key, item])
      );

      const changedCustomVitals = customVitals.filter((metric) => {
        const numericValue = parseNumericValue(metric.value);

        if (numericValue === null || !String(metric.label || "").trim()) {
          return false;
        }

        const initialMetric = initialCustomByKey.get(metric.key);

        return (
          !initialMetric ||
          !valuesAreEqual(initialMetric.value, metric.value) ||
          String(initialMetric.unit || "").trim() !== String(metric.unit || "").trim() ||
          String(initialMetric.label || "").trim() !== String(metric.label || "").trim()
        );
      });

      if (!changedFields.length && !changedCustomVitals.length) {
        return false;
      }

      setSavingVitals(true);
      setVitalsError("");

      const config = getDoctorMedicalRequestConfig();

      try {
        const measuredAt = new Date().toISOString();
        const requests = [];

        changedFields.forEach((field) => {
          if (field.isBloodPressure) {
            const { sys, dia } = parseBloodPressure(vitals.bloodPressure);

            if (sys === null || dia === null) {
              throw new Error(
                "La tension artérielle doit être au format SYS/DIA, par exemple 120/80."
              );
            }

            requests.push(
              axiosMedical.post(
                `/${cleanUserId}/vitals`,
                {
                  type: "BP_SYS",
                  value: sys,
                  unit: "mmHg",
                  measuredAt,
                },
                config
              )
            );

            requests.push(
              axiosMedical.post(
                `/${cleanUserId}/vitals`,
                {
                  type: "BP_DIA",
                  value: dia,
                  unit: "mmHg",
                  measuredAt,
                },
                config
              )
            );

            return;
          }

          const numericValue = parseNumericValue(vitals[field.key]);

          if (numericValue === null) {
            return;
          }

          requests.push(
            axiosMedical.post(
              `/${cleanUserId}/vitals`,
              {
                type: field.type,
                value: numericValue,
                unit: field.unit,
                measuredAt,
              },
              config
            )
          );
        });

        changedCustomVitals.forEach((metric) => {
          const numericValue = parseNumericValue(metric.value);

          requests.push(
            axiosMedical.post(
              `/${cleanUserId}/vitals`,
              {
                type: "OTHER",
                customLabel: String(metric.label).trim(),
                value: numericValue,
                unit: String(metric.unit || "").trim() || undefined,
                measuredAt,
              },
              config
            )
          );
        });

        if (requests.length) {
          await Promise.all(requests);
        }

        initialVitalsRef.current = { ...vitals };
        initialCustomVitalsRef.current = customVitals.map((item) => ({
          ...item,
          isNew: false,
        }));

        window.dispatchEvent(
          new CustomEvent("patient-medical-measurements-updated", {
            detail: {
              userId: cleanUserId,
              at: Date.now(),
            },
          })
        );

        try {
          localStorage.setItem(
            "patient-medical-measurements-updated",
            JSON.stringify({
              userId: cleanUserId,
              at: Date.now(),
            })
          );
        } catch {
          // localStorage indisponible
        }

        console.log("✅ Doctor consultation - patient vitals saved:", {
          userId: cleanUserId,
          standard: changedFields.map((field) => field.key),
          custom: changedCustomVitals.map((metric) => metric.label),
        });

        return true;
      } catch (error) {
        console.error(
          "❌ Save patient vitals error:",
          error?.response?.data || error
        );

        const backendMessage = error?.response?.data?.message;
        const message = Array.isArray(backendMessage)
          ? backendMessage.join(", ")
          : backendMessage;

        setVitalsError(
          message ||
            error?.message ||
            "Impossible d'enregistrer les mesures du patient."
        );

        throw error;
      } finally {
        setSavingVitals(false);
      }
    }, [userId, vitals, customVitals]);

  const handleStartVitalsEdit = useCallback(() => {
    setVitalsError("");
    setVitalsSuccess("");
    setIsEditingVitals(true);
  }, []);

  const handleCustomVitalValueChange = useCallback((key, value) => {
    const clean = String(value).replace(/[^\d.,-]/g, "");

    setCustomVitals((previous) =>
      previous.map((metric) =>
        metric.key === key
          ? { ...metric, value: clean }
          : metric
      )
    );

    setVitalsError("");
    setVitalsSuccess("");
  }, []);

  const handleAddCustomVital = useCallback((data) => {
    const label = String(data?.label || "").trim();
    const unit = String(data?.unit || "").trim();
    const numericValue = parseNumericValue(data?.value);

    if (!label || numericValue === null) {
      setVitalsError("Le nom et la valeur de la mesure sont obligatoires.");
      return;
    }

    const key = makeCustomVitalKey(label, unit);

    setCustomVitals((previous) => {
      const existing = previous.find((metric) => metric.key === key);

      if (existing) {
        return previous.map((metric) =>
          metric.key === key
            ? { ...metric, value: String(numericValue) }
            : metric
        );
      }

      return [
        ...previous,
        {
          key,
          backendId: null,
          label,
          unit,
          value: String(numericValue),
          measuredAt: null,
          isNew: true,
        },
      ];
    });

    setAddVitalModalOpen(false);
    setIsEditingVitals(true);
    setVitalsError("");
    setVitalsSuccess("");
  }, []);

  const handleOpenAddVital = useCallback(() => {
    setIsEditingVitals(true);
    setAddVitalModalOpen(true);
    setVitalsError("");
    setVitalsSuccess("");
  }, []);

  const handleSaveVitals = useCallback(async () => {
    try {
      const changed = await saveChangedPatientVitals();

      setIsEditingVitals(false);

      setVitalsSuccess(
        changed
          ? "Mesures enregistrées avec succès."
          : "Aucune modification à enregistrer."
      );

      window.setTimeout(() => {
        setVitalsSuccess("");
      }, 3000);
    } catch {
      // Le message backend est déjà placé dans vitalsError.
    }
  }, [saveChangedPatientVitals]);

  const handleVitalsCardBlur = useCallback((event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    if (!isEditingVitals || savingVitals || addVitalModalOpen) return;
    void handleSaveVitals();
  }, [addVitalModalOpen, handleSaveVitals, isEditingVitals, savingVitals]);

  const handleVitalsCardClick = useCallback((event) => {
    if (event.target.closest("button")) return;
    handleStartVitalsEdit();
  }, [handleStartVitalsEdit]);

  /* =======================================================
     ZONE UI
  ======================================================= */

  const [
    activeZoneId,
    setActiveZoneId,
  ] = useState(null);

  const [
    editingTitleId,
    setEditingTitleId,
  ] = useState(null);

  const [
    editingTitleValue,
    setEditingTitleValue,
  ] = useState("");

  /* =======================================================
     REFS
  ======================================================= */

  const textareasRef = useRef({});

  const titleInputRef = useRef({});

  const dictationRef = useRef(null);

  const tokenAbortRef = useRef(null);

  const operationRef = useRef(false);

  const stopRequestedRef = useRef(false);

  const finalSegmentsRef = useRef(
    new Set()
  );

  const lastFinalTranscriptRef = useRef({
    text: "",
    at: 0,
  });

  const hasDictationInputRef = useRef(false);

  const dictationZoneRef = useRef(
    "diagnostic"
  );

  /* =======================================================
     DICTATION
  ======================================================= */

  const [
    dictationStatus,
    setDictationStatus,
  ] = useState("idle");

  const [
    dictationZoneId,
    setDictationZoneId,
  ] = useState(null);

  const [
    interimTranscript,
    setInterimTranscript,
  ] = useState("");

  const [
    dictationError,
    setDictationError,
  ] = useState("");

  const [, setAudioLevel] = useState(0);

  /* =======================================================
     CONSULTATION PRICE
  ======================================================= */

  const consultationPrice = 70;

  /* =======================================================
     DICTATION LANGUAGE
  ======================================================= */

  const dictationLanguage = useMemo(() => {
    const language = String(
      i18n?.resolvedLanguage ||
        i18n?.language ||
        "fr"
    ).toLowerCase();

    return language.startsWith("en")
      ? "en"
      : "fr";
  }, [
    i18n?.language,
    i18n?.resolvedLanguage,
  ]);

  const isDictationConnecting =
    dictationStatus === "connecting";

  const isDictationRecording =
    dictationStatus === "recording";

  const isDictationActive =
    isDictationConnecting ||
    isDictationRecording;

  /* =======================================================
     DIAGNOSTIC TEXT
  ======================================================= */

  const diagnosticText = useMemo(() => {
    return (
      zones.find(
        (zone) =>
          zone.id === "diagnostic"
      )?.value || ""
    );
  }, [zones]);

  /* =======================================================
     TOTAL FRONT
  ======================================================= */

  useEffect(() => {
    onTotalChange?.({
      consultationPrice,

      medicalActsTotal: 0,

      total: consultationPrice,
    });
  }, [
    consultationPrice,
    onTotalChange,
  ]);

  /* =======================================================
     TRANSLATION UPDATE
  ======================================================= */

  useEffect(() => {
    setZones((previous) => {
      const defaults =
        buildDefaultZones(t);

      const map = new Map(
        defaults.map(
          (defaultZone) => [
            defaultZone.id,
            defaultZone,
          ]
        )
      );

      return previous.map((zone) => {
        const defaultZone =
          map.get(zone.id);

        if (!defaultZone) {
          return zone;
        }

        return {
          ...zone,

          title:
            defaultZone.title,

          placeholder:
            defaultZone.placeholder,

          example:
            defaultZone.example,
        };
      });
    });
  }, [t]);

  /* =======================================================
     TITLE FOCUS
  ======================================================= */

  useEffect(() => {
    if (!editingTitleId) {
      return;
    }

    requestAnimationFrame(() => {
      titleInputRef.current[
        editingTitleId
      ]?.focus();
    });
  }, [editingTitleId]);

  /* =======================================================
     ZONE METHODS
  ======================================================= */

  const toggleZone = (id) => {
    setOpenZones((previous) => ({
      ...previous,

      [id]:
        !previous[id],
    }));
  };

  const openZone = (id) => {
    setOpenZones((previous) => ({
      ...previous,

      [id]: true,
    }));
  };

  const focusZone = (id) => {
    openZone(id);

    setActiveZoneId(id);

    requestAnimationFrame(() => {
      textareasRef.current?.[
        id
      ]?.focus();
    });
  };

  /* =======================================================
     ADD ZONE
  ======================================================= */

  const handleAddZone = () => {
    const newId = uid();

    setZones((previous) => [
      ...previous,

      {
        id: newId,

        title: t(
          "addConsultation.zones.custom.title",
          {
            index:
              previous.length + 1,

            defaultValue: `Zone ${
              previous.length + 1
            }`,
          }
        ),

        placeholder: t(
          "addConsultation.zones.custom.placeholder",
          {
            defaultValue:
              "Write here...",
          }
        ),

        example: "",

        value: "",
      },
    ]);

    setOpenZones((previous) => ({
      ...previous,

      [newId]: true,
    }));

    setTimeout(() => {
      setEditingTitleId(
        newId
      );

      setEditingTitleValue(
        ""
      );
    }, 0);
  };

  /* =======================================================
     CHANGE ZONE
  ======================================================= */

  const handleChange = (
    id,
    value
  ) => {
    setZones((previous) =>
      previous.map((zone) =>
        zone.id === id
          ? {
              ...zone,

              value,
            }
          : zone
      )
    );
  };

  /* =======================================================
     VITAL CHANGE
  ======================================================= */

  const handleVitalChange = (
    key,
    value
  ) => {
    const sanitized =
      key === "bloodPressure"
        ? String(value ?? "").replace(/[^\d.,\/-]/g, "")
        : String(value ?? "").replace(/[^\d.,-]/g, "");

    setVitals((previous) => ({
      ...previous,

      [key]: sanitized,
    }));

    if (vitalsError) {
      setVitalsError("");
    }
  };

  /* =======================================================
     RESET DICTATION
  ======================================================= */

  const resetDictationSession =
    useCallback(() => {
      setInterimTranscript(
        ""
      );

      setDictationError("");

      setAudioLevel(0);

      finalSegmentsRef.current.clear();

      lastFinalTranscriptRef.current = {
        text: "",
        at: 0,
      };
    }, []);

  /* =======================================================
     APPEND FINAL TRANSCRIPT
  ======================================================= */

  const appendFinalTranscript =
    useCallback((data) => {
      const cleanText = String(
        data?.text || ""
      ).trim();

      if (!cleanText) {
        return;
      }

      const segmentKey = `${cleanText}|${
        data?.start ?? ""
      }|${data?.end ?? ""}`;

      const now = Date.now();

      const last =
        lastFinalTranscriptRef.current;

      if (
        finalSegmentsRef.current.has(
          segmentKey
        )
      ) {
        return;
      }

      if (
        last.text === cleanText &&
        now - last.at < 2000
      ) {
        return;
      }

      finalSegmentsRef.current.add(
        segmentKey
      );

      if (
        finalSegmentsRef.current.size >
        120
      ) {
        const firstKey =
          finalSegmentsRef.current
            .values()
            .next().value;

        finalSegmentsRef.current.delete(
          firstKey
        );
      }

      lastFinalTranscriptRef.current = {
        text: cleanText,
        at: now,
      };

      hasDictationInputRef.current =
        true;

      const targetZoneId =
        dictationZoneRef.current;

      setZones((previous) =>
        previous.map((zone) => {
          if (
            zone.id !==
            targetZoneId
          ) {
            return zone;
          }

          const separator =
            zone.value &&
            !/[\s\n]$/.test(
              zone.value
            )
              ? "\n"
              : "";

          return {
            ...zone,

            value: `${
              zone.value || ""
            }${separator}${cleanText}`,
          };
        })
      );
    }, []);

  /* =======================================================
     DICTATION ERROR
  ======================================================= */

  const formatDictationError =
    useCallback((error) => {
      const rawMessage =
        error?.response?.data
          ?.message ||
        error?.detail?.message ||
        error?.message ||
        "";

      const lowerMessage =
        String(
          rawMessage
        ).toLowerCase();

      if (
        error?.name ===
          "NotAllowedError" ||
        lowerMessage.includes(
          "permission"
        ) ||
        lowerMessage.includes(
          "denied"
        )
      ) {
        return "Micro refusé. Autorisez le microphone puis relancez la dictée.";
      }

      if (
        error?.name ===
          "AbortError" ||
        lowerMessage.includes(
          "canceled"
        )
      ) {
        return "";
      }

      return (
        rawMessage ||
        "Impossible de démarrer la dictée."
      );
    }, []);

  /* =======================================================
     STOP DICTATION
  ======================================================= */

  const stopDictation =
    useCallback(
      async ({
        silent = false,
      } = {}) => {
        stopRequestedRef.current =
          true;

        tokenAbortRef.current?.abort();

        tokenAbortRef.current =
          null;

        const dictation =
          dictationRef.current;

        if (!dictation) {
          return;
        }

        try {
          if (!silent) {
            setDictationStatus(
              (current) =>
                current ===
                  "recording" ||
                current ===
                  "connecting"
                  ? "connecting"
                  : current
            );
          }

          dictation.stopRecording?.();

          await dictation.closeConnection?.();

          setInterimTranscript(
            ""
          );

          setAudioLevel(0);

          setDictationStatus(
            "disconnected"
          );

          setDictationZoneId(
            null
          );
        } catch (error) {
          if (!silent) {
            setDictationStatus(
              "error"
            );

            setDictationError(
              formatDictationError(
                error
              )
            );
          }
        }
      },
      [
        formatDictationError,
      ]
    );

  /* =======================================================
     START DICTATION

     NB:
     La dictée conserve son API Corti.
     Le bouton NEXT, lui, ne consomme aucune API.
  ======================================================= */

  const startDictation =
    useCallback(
      async (zoneId) => {
        if (
          operationRef.current ||
          isDictationRecording
        ) {
          return;
        }

        const dictation =
          dictationRef.current;

        if (!dictation) {
          return;
        }

        if (
          !window.navigator
            ?.mediaDevices
            ?.getUserMedia
        ) {
          setDictationStatus(
            "error"
          );

          setDictationError(
            "Le navigateur ne supporte pas l'accès au microphone."
          );

          return;
        }

        operationRef.current =
          true;

        stopRequestedRef.current =
          false;

        const controller =
          new AbortController();

        tokenAbortRef.current =
          controller;

        try {
          setDictationStatus(
            "connecting"
          );

          resetDictationSession();

          dictationZoneRef.current =
            zoneId;

          setDictationZoneId(
            zoneId
          );

          openZone(zoneId);

          setActiveZoneId(
            zoneId
          );

          const response =
            await axiosPrescription.post(
              "/prescriptions/corti/browser-token",

              {
                language:
                  dictationLanguage,
              },

              {
                signal:
                  controller.signal,
              }
            );

          if (
            stopRequestedRef.current
          ) {
            return;
          }

          const token =
            response?.data?.token;

          if (!token) {
            throw new Error(
              "Token Corti manquant."
            );
          }

          dictation.accessToken =
            token;

          dictation.settingsEnabled =
            [];

          dictation.allowButtonFocus =
            true;

          dictation.pushToTalkKeybinding =
            undefined;

          dictation.toggleToTalkKeybinding =
            undefined;

          dictation.dictationConfig = {
            automaticPunctuation:
              true,

            interimResults:
              true,

            primaryLanguage:
              dictationLanguage,

            spokenPunctuation:
              true,
          };

          dictation.startRecording?.();

          setDictationStatus(
            "recording"
          );
        } catch (error) {
          const message =
            formatDictationError(
              error
            );

          if (message) {
            setDictationStatus(
              "error"
            );

            setDictationError(
              message
            );
          } else {
            setDictationStatus(
              "disconnected"
            );
          }
        } finally {
          operationRef.current =
            false;

          tokenAbortRef.current =
            null;
        }
      },
      [
        dictationLanguage,
        formatDictationError,
        isDictationRecording,
        resetDictationSession,
      ]
    );

  /* =======================================================
     TOGGLE DICTATION
  ======================================================= */

  const toggleDictation =
    useCallback(
      async (zoneId) => {
        if (
          isDictationRecording ||
          isDictationConnecting
        ) {
          await stopDictation();

          if (
            dictationZoneRef.current ===
            zoneId
          ) {
            return;
          }
        }

        await startDictation(
          zoneId
        );
      },
      [
        isDictationConnecting,
        isDictationRecording,
        startDictation,
        stopDictation,
      ]
    );

  /* =======================================================
     CORTI EVENTS
  ======================================================= */

  useEffect(() => {
    const dictation =
      dictationRef.current;

    if (!dictation) {
      return undefined;
    }

    const handleTranscript = (
      event
    ) => {
      const data =
        getTranscriptData(
          event
        );

      const text = String(
        data?.text || ""
      ).trim();

      if (!text) {
        return;
      }

      if (data?.isFinal) {
        setInterimTranscript(
          ""
        );

        appendFinalTranscript(
          data
        );
      } else {
        setInterimTranscript(
          text
        );
      }
    };

    const handleRecordingState = (
      event
    ) => {
      const recordingState =
        event?.detail?.state;

      if (
        recordingState ===
          "initializing" ||
        recordingState ===
          "stopping"
      ) {
        setDictationStatus(
          "connecting"
        );
      } else if (
        recordingState ===
        "recording"
      ) {
        setDictationStatus(
          "recording"
        );
      } else if (
        recordingState ===
        "stopped"
      ) {
        setDictationStatus(
          "disconnected"
        );

        setAudioLevel(0);

        setDictationZoneId(
          null
        );
      }
    };

    const handleAudioLevel = (
      event
    ) => {
      setAudioLevel(
        clampAudioLevel(
          event?.detail
            ?.audioLevel
        )
      );
    };

    const handleDictationError = (
      event
    ) => {
      const message =
        formatDictationError(
          event
        );

      if (!message) {
        return;
      }

      setDictationStatus(
        "error"
      );

      setDictationError(
        message
      );

      setAudioLevel(0);
    };

    dictation.addEventListener(
      "transcript",
      handleTranscript
    );

    dictation.addEventListener(
      "recording-state-changed",
      handleRecordingState
    );

    dictation.addEventListener(
      "audio-level-changed",
      handleAudioLevel
    );

    dictation.addEventListener(
      "error",
      handleDictationError
    );

    return () => {
      dictation.removeEventListener(
        "transcript",
        handleTranscript
      );

      dictation.removeEventListener(
        "recording-state-changed",
        handleRecordingState
      );

      dictation.removeEventListener(
        "audio-level-changed",
        handleAudioLevel
      );

      dictation.removeEventListener(
        "error",
        handleDictationError
      );
    };
  }, [
    appendFinalTranscript,
    formatDictationError,
  ]);

  /* =======================================================
     PAGE VISIBILITY
  ======================================================= */

  useEffect(() => {
    const handleVisibilityChange =
      () => {
        if (document.hidden) {
          stopDictation({
            silent: true,
          });
        }
      };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange
    );

    return () =>
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
  }, [stopDictation]);

  /* =======================================================
     CLEANUP
  ======================================================= */

  useEffect(() => {
    return () => {
      tokenAbortRef.current?.abort();

      const dictation =
        dictationRef.current;

      dictation?.stopRecording?.();

      dictation?.closeConnection?.();
    };
  }, []);

  /* =======================================================
     REMOVE ZONE
  ======================================================= */

  const handleRemoveZone = (
    id
  ) => {
    setZones((previous) =>
      previous.filter(
        (zone) =>
          zone.id !== id
      )
    );

    setOpenZones((previous) => {
      const copy = {
        ...previous,
      };

      delete copy[id];

      return copy;
    });

    delete textareasRef.current[
      id
    ];

    if (
      activeZoneId === id
    ) {
      setActiveZoneId(
        null
      );
    }
  };

  /* =======================================================
     EDIT TITLE
  ======================================================= */

  const startEditingTitle = (
    id,
    title
  ) => {
    openZone(id);

    setEditingTitleId(id);

    setEditingTitleValue(
      title
    );
  };

  const cancelEditingTitle =
    () => {
      setEditingTitleId(
        null
      );

      setEditingTitleValue(
        ""
      );
    };

  const saveTitle = (id) => {
    const value =
      editingTitleValue.trim();

    if (value) {
      setZones((previous) =>
        previous.map((zone) =>
          zone.id === id
            ? {
                ...zone,

                title:
                  value,
              }
            : zone
        )
      );
    }

    cancelEditingTitle();
  };

  /* =======================================================
     BUILD CONSULTATION PAYLOAD

     Backend Consultation entity:
     - reason UI -> title
     - text zones -> sections[]
     - vitals are NOT stored here; they are saved in
       medical-information by saveChangedPatientVitals().
  ======================================================= */

  const buildConsultationPayload = () => {
    const consultationDateTime = new Date(
      `${consultationDate}T${consultationTime}:00`
    );

    if (Number.isNaN(consultationDateTime.getTime())) {
      throw new Error("Date ou heure de consultation invalide.");
    }

    return {
      userId: String(userId || "").trim(),

      doctorId: String(doctorId || "").trim(),

      consultationDateTime: consultationDateTime.toISOString(),

      consultationPrice: Number(consultationPrice || 0),

      title:
        consultationReason.trim() ||
        "Consultation",

      sections: zones
        .filter((zone) =>
          String(zone?.value || "").trim()
        )
        .map((zone) => ({
          title:
            String(zone?.title || "").trim() ||
            "Section",

          description: String(
            zone?.value || ""
          ).trim(),
        })),
    };
  };

  /* =======================================================
     NEXT

     1. Save modified patient vitals in medical-information.
     2. POST the consultation text zones to /consultations.
     3. Recover the backend consultation UUID.
     4. Open the prescription step with this real UUID.
  ======================================================= */

  const handleNext = async () => {
    const userIdStr = String(
      userId || ""
    ).trim();

    const doctorIdStr = String(
      doctorId || ""
    ).trim();

    if (!userIdStr || !doctorIdStr) {
      setConsultationSaveError(
        "Patient ID ou Doctor ID manquant."
      );
      return;
    }

    setConsultationSaveError("");

    try {
      await stopDictation({
        silent: true,
      });

      /* -----------------------------------------------
         A. VITALS -> medical-information
      ------------------------------------------------ */

      await saveChangedPatientVitals();
      setIsEditingVitals(false);

      /* -----------------------------------------------
         B. TEXT ZONES -> consultation.sections
      ------------------------------------------------ */

      const payload =
        buildConsultationPayload();

      console.log(
        "📤 CREATE CONSULTATION:",
        payload
      );

      const result =
        await createConsultation(
          payload
        );

      if (
        result?.type?.includes(
          "rejected"
        )
      ) {
        throw new Error(
          getConsultationErrorMessage(
            result
          )
        );
      }

      const consultationId =
        extractConsultationId(
          result
        );

      if (!consultationId) {
        console.error(
          "❌ Consultation créée mais ID introuvable:",
          result
        );

        throw new Error(
          "La consultation a été créée mais aucun ID n'a été retourné."
        );
      }

      console.log(
        "✅ CONSULTATION CREATED:",
        consultationId
      );

      /* Refresh History / consultation list */
      await onSuccess?.(consultationId);

      window.dispatchEvent(
        new CustomEvent(
          "consultations-updated",
          {
            detail: {
              patientId:
                userIdStr,

              consultationId,
            },
          }
        )
      );

      /* -----------------------------------------------
         C. OPEN PRESCRIPTION STEP
      ------------------------------------------------ */

      const sourceText =
        diagnosticText.trim();

      const source =
        hasDictationInputRef.current
          ? "voice"
          : "manual";

      const consultationDraft = {
        frontOnly: false,

        consultationId,

        ...payload,

        reason:
          consultationReason.trim(),

        consultationDate,
        consultationTime,

        /*
         * Kept only for the next front step.
         * These values are already persisted separately
         * in medical-information, not in Consultation.
         */
        vitals: {
          ...vitals,
        },

        customVitals:
          customVitals.map(
            (item) => ({
              ...item,
            })
          ),

        medicalActs: [],

        diagnosticText:
          sourceText,

        documentTexts: {
          ...EMPTY_DOCUMENT_TEXTS,
        },

        savedDocuments: {},

        classificationResult:
          null,

        alreadyClassified:
          false,

        source,

        language:
          dictationLanguage,
      };

      onOpenPrescribePopup?.(
        consultationId,
        consultationDraft
      );
    } catch (error) {
      const message =
        getConsultationErrorMessage(
          error
        );

      console.error(
        "❌ CREATE CONSULTATION ERROR:",
        error
      );

      setConsultationSaveError(
        message
      );
    }
  };

  /* =======================================================
     DOCUMENTS
  ======================================================= */

  const visibleDocuments =
    documentsForPreview.slice(
      0,
      2
    );

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <section
      className="anc-wrap"
      dir={
        isRTL
          ? "rtl"
          : "ltr"
      }
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <div className="anc-top">
        <h2 className="anc-title">
          {t(
            "addConsultation.title",
            {
              defaultValue:
                "Add New Consultation",
            }
          )}
        </h2>

        {isEditingDateTime ? (
          <div className="anc-dateTimeEdit">
            <input
              type="date"
              value={
                consultationDate
              }
              onChange={(
                event
              ) =>
                setConsultationDate(
                  event.target
                    .value
                )
              }
            />

            <input
              type="time"
              className="anc-fieldInputTime"
              value={
                consultationTime
              }
              onChange={(
                event
              ) =>
                setConsultationTime(
                  event.target
                    .value
                )
              }
            />

            <button
              type="button"
              className="anc-dateTimeDone"
              onClick={() =>
                setIsEditingDateTime(
                  false
                )
              }
            >
              {t(
                "common.done",
                {
                  defaultValue:
                    "OK",
                }
              )}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="anc-dateTimePill"
            onClick={() =>
              setIsEditingDateTime(
                true
              )
            }
          >
            <span className="anc-dateTimeSeg">
              <Calendar
                size={14}
              />

              {formatDatePretty(
                consultationDate
              )}
            </span>

            <ChevronDown
              size={14}
            />

            <span className="anc-dateTimeDivider" />

            <span className="anc-dateTimeSeg">
              <Clock
                size={14}
              />

              {formatTimePretty(
                consultationTime
              )}
            </span>
          </button>
        )}
      </div>

      {/* =================================================
          REASON (seul, sur sa propre ligne)
          + VITALS (regroupés sur une autre ligne)
      ================================================= */}

      <div className="anc-fieldsRow">
        <div className="anc-fieldGroup anc-reasonField anc-reasonFullRow">
          <label className="anc-fieldLabel">
            {t(
              "addConsultation.fields.reason",
              {
                defaultValue:
                  "Reason",
              }
            )}
          </label>

          <input
            className="anc-fieldInput"
            value={
              consultationReason
            }
            onChange={(
              event
            ) =>
              setConsultationReason(
                event.target
                  .value
              )
            }
            placeholder={t(
              "addConsultation.fields.reasonPh",
              {
                defaultValue:
                  "Reason for consultation",
              }
            )}
          />
        </div>

        <div
          className={`anc-vitalsEditCard ${isEditingVitals ? "is-editing" : "is-readonly"}`}
          onBlurCapture={handleVitalsCardBlur}
        >
          <div className="anc-vitalsToolbar">
            <div className="anc-vitalsToolbarText">
              <strong>{t("addConsultation.vitals.title")}</strong>
            <span>
              {hasAnyVital
                ? t("addConsultation.vitals.latest")
                : t("addConsultation.vitals.empty")}
            </span>
            </div>

            <div className="anc-vitalsToolbarActions">
              {isEditingVitals && hasUnsavedVitalChanges && (
                <button
                  type="button"
                  className="anc-vitalsSaveBtn"
                  onClick={handleSaveVitals}
                  disabled={loadingVitals || savingVitals}
                >
                  {savingVitals ? t("addConsultation.vitals.saving") : t("addConsultation.vitals.save")}
                </button>
              )}
            </div>
          </div>

        <div className={`anc-vitalsRow ${isEditingVitals ? "is-editing" : "is-readonly"}`}>
          {VITAL_FIELDS.map(
            (field) => (
              <div
                className="anc-fieldGroup anc-vitalField"
                key={
                  field.key
                }
                onClick={handleVitalsCardClick}
                title={!isEditingVitals ? t("addConsultation.vitals.clickToEdit") : undefined}
              >
                <label
                  className="anc-fieldLabel"
                  title={t(
                    field.labelKey,
                    {
                      defaultValue:
                        field.defaultLabel,
                    }
                  )}
                >
                  {t(
                    field.labelKey,
                    {
                      defaultValue:
                        field.defaultLabel,
                    }
                  )}
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  className={`anc-fieldInput anc-vitalInput ${isEditingVitals ? "is-editing" : "is-readonly"}`}
                  value={
                    vitals[
                      field.key
                    ]
                  }
                  onChange={(
                    event
                  ) =>
                    handleVitalChange(
                      field.key,
                      event.target
                        .value
                    )
                  }
                  placeholder={
                    loadingVitals
                      ? "..."
                      : field.isBloodPressure
                      ? "120/80"
                      : "---"
                  }
                  disabled={
                    !isEditingVitals ||
                    loadingVitals ||
                    savingVitals
                  }
                />
              </div>
            )
          )}

          {customVitals.map((metric) => (
            <div
              className="anc-fieldGroup anc-vitalField anc-customVitalField"
              key={metric.key}
              onClick={handleVitalsCardClick}
              title={!isEditingVitals ? "Cliquez pour modifier" : undefined}
            >
              <label
                className="anc-fieldLabel"
                title={`${metric.label}${metric.unit ? ` (${metric.unit})` : ""}`}
              >
                {metric.label.replace(/[_-]+/g, " ")}
              </label>

              <div className="anc-customVitalInputWrap">
                <input
                  type="text"
                  inputMode="decimal"
                  className={`anc-fieldInput anc-vitalInput ${
                    isEditingVitals ? "is-editing" : "is-readonly"
                  }`}
                  value={metric.value}
                  onChange={(event) =>
                    handleCustomVitalValueChange(metric.key, event.target.value)
                  }
                  placeholder={t("addConsultation.vitals.noValue")}
                  disabled={
                    !isEditingVitals ||
                    loadingVitals ||
                    savingVitals
                  }
                />
                {metric.unit ? (
                  <span className="anc-customVitalUnit">{metric.unit}</span>
                ) : null}
              </div>
            </div>
          ))}

          <button
            type="button"
            className="anc-vitalPlusCard"
            onClick={handleOpenAddVital}
            disabled={loadingVitals || savingVitals}
            title={t("addConsultation.vitals.addMetric")}
            aria-label={t("addConsultation.vitals.addMetric")}
          >
            <Plus size={20} />
          </button>
        </div>

        {vitalsError && (
          <div className="anc-vitalsError">
            {vitalsError}
          </div>
        )}

          {vitalsSuccess && (
            <div className="anc-vitalsSuccess">
              {vitalsSuccess}
            </div>
          )}
        </div>
      </div>

      {/* =================================================
          RECENT MEDICAL RECORDS
      ================================================= */}

      <div className="anc-recordsCard">
        <div className="anc-recordsHeader">
          <div>
            <h4 className="anc-recordsTitle">
              {t(
                "addConsultation.records.title",
                {
                  defaultValue:
                    "Recent Medical Records",
                }
              )}
            </h4>

            <p className="anc-recordsSubtitle">
              {t(
                "addConsultation.records.subtitle",
                {
                  defaultValue:
                    "uploaded medical documents, scans, X-rays, prescriptions, etc.",
                }
              )}
            </p>
          </div>

          {documentsForPreview.length >
            0 && (
            <button
              type="button"
              className="anc-recordsViewAll"
              onClick={
                onViewAllDocuments
              }
            >
              {t(
                "addConsultation.records.viewAll",
                {
                  defaultValue:
                    "View all",
                }
              )}{" "}
              →
            </button>
          )}
        </div>

        {visibleDocuments.length >
        0 ? (
          <div className="anc-recordsList">
            {visibleDocuments.map(
              (doc) => (
                <div
                  className="anc-recordItem"
                  key={
                    doc.id
                  }
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    if (doc.fileUrl) window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
                  }}
                  onKeyDown={(event) => {
                    if ((event.key === "Enter" || event.key === " ") && doc.fileUrl) {
                      event.preventDefault();
                      window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  <div className="anc-recordMain">
                    <span className="anc-recordIcon">
                      <FileText
                        size={
                          16
                        }
                      />
                    </span>

                    <div className="anc-recordText">
                      <span className="anc-recordName">
                        {
                          doc.name
                        }
                      </span>

                      <span className="anc-recordDate">
                        {
                          doc.date
                        }
                      </span>
                    </div>
                  </div>

                  <div className="anc-recordRight">
                    {doc.status && (
                      <span className="anc-recordBadge">
                        {
                          doc.status
                        }
                      </span>
                    )}

                    <span className="anc-recordViewLabel">{t("addConsultation.records.view")}</span>
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="anc-recordsEmpty">
            {t(
              "addConsultation.records.empty",
              {
                defaultValue:
                  "No documents yet.",
              }
            )}
          </div>
        )}

        <button
          type="button"
          className="anc-addDocBtn"
          onClick={onAddDocument}
        >
          <Plus size={14} />
          {t("addConsultation.records.add", {
            defaultValue: "Add New medical document",
          })}
        </button>
      </div>

      {/* =================================================
          TEXT ZONES
      ================================================= */}

      <div className="anc-zonesList">
        {zones.map(
          (zone) => {
            const isOpen =
              !!openZones[
                zone.id
              ];

            const isEmpty =
              !zone.value.trim();

            const isActive =
              activeZoneId ===
              zone.id;

            const isCustomZone =
              zone.id !==
                "history" &&
              zone.id !==
                "diagnostic";

            const isThisZoneDictating =
              dictationZoneId ===
                zone.id &&
              isDictationActive;

            return (
              <div
                key={
                  zone.id
                }
                className="anc-card"
              >
                <div className="anc-cardInner">
                  <div
                    className="anc-cardTopRow"
                    onClick={() =>
                      toggleZone(
                        zone.id
                      )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(
                      event
                    ) => {
                      if (
                        event.key ===
                        "Enter"
                      ) {
                        toggleZone(
                          zone.id
                        );
                      }
                    }}
                  >
                    <div className="anc-cardTitleWrapper">
                      {editingTitleId ===
                      zone.id ? (
                        <input
                          ref={(
                            element
                          ) => {
                            titleInputRef.current[
                              zone.id
                            ] =
                              element;
                          }}
                          className="anc-cardTitleInput"
                          value={
                            editingTitleValue
                          }
                          onClick={(
                            event
                          ) =>
                            event.stopPropagation()
                          }
                          onChange={(
                            event
                          ) =>
                            setEditingTitleValue(
                              event.target
                                .value
                            )
                          }
                          onBlur={() =>
                            saveTitle(
                              zone.id
                            )
                          }
                          onKeyDown={(
                            event
                          ) => {
                            event.stopPropagation();

                            if (
                              event.key ===
                              "Escape"
                            ) {
                              cancelEditingTitle();
                            }

                            if (
                              event.key ===
                              "Enter"
                            ) {
                              saveTitle(
                                zone.id
                              );
                            }
                          }}
                        />
                      ) : (
                        <div
                          className="anc-cardTitle"
                          onClick={(
                            event
                          ) => {
                            event.stopPropagation();

                            startEditingTitle(
                              zone.id,
                              zone.title
                            );
                          }}
                        >
                          {
                            zone.title
                          }
                        </div>
                      )}
                    </div>

                    {/* DICTATION */}

                    <div
                      className="anc-dictationControls"
                      onClick={(
                        event
                      ) =>
                        event.stopPropagation()
                      }
                    >
                      <div
                        className={`anc-autoDictate ${
                          isThisZoneDictating
                            ? "active"
                            : ""
                        }`}
                      >
                        <span className="anc-autoDictateLabel">
                          {t(
                            "addConsultation.dictation.auto",
                            {
                              defaultValue:
                                "Auto-Dictate",
                            }
                          )}
                        </span>

                        <span
                          className={`anc-switch ${
                            isThisZoneDictating
                              ? "on"
                              : ""
                          } ${
                            dictationZoneId ===
                              zone.id &&
                            isDictationConnecting
                              ? "connecting"
                              : ""
                          } ${
                            dictationZoneId ===
                              zone.id &&
                            dictationStatus ===
                              "error"
                              ? "errorState"
                              : ""
                          }`}
                        />
                      </div>

                      <button
                        type="button"
                        className={`anc-micBtn ${
                          dictationZoneId ===
                            zone.id &&
                          isDictationRecording
                            ? "recording"
                            : ""
                        } ${
                          dictationZoneId ===
                            zone.id &&
                          isDictationConnecting
                            ? "connecting"
                            : ""
                        }`}
                        onClick={() =>
                          toggleDictation(
                            zone.id
                          )
                        }
                        aria-label={
                          dictationZoneId ===
                            zone.id &&
                          isDictationRecording
                            ? t("addConsultation.dictation.stop")
                            : t("addConsultation.dictation.start")
                        }
                      >
                        {dictationZoneId ===
                          zone.id &&
                        isDictationConnecting ? (
                          <Loader2
                            size={
                              15
                            }
                          />
                        ) : dictationZoneId ===
                            zone.id &&
                          isDictationRecording ? (
                          <Square
                            size={
                              13
                            }
                          />
                        ) : (
                          <Mic
                            size={
                              15
                            }
                          />
                        )}
                      </button>

                      {isCustomZone && (
                        <button
                          type="button"
                          className="anc-close"
                          onClick={() =>
                            handleRemoveZone(
                              zone.id
                            )
                          }
                          aria-label={t("addConsultation.actions.removeZone")}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>

                  {/* CONTENT */}

                  {isOpen && (
                    <div className="anc-contentArea">
                      {isEmpty &&
                        !isActive && (
                          <div
                            className="anc-placeholderOverlay"
                            onClick={() =>
                              focusZone(
                                zone.id
                              )
                            }
                          >
                            <div className="anc-q">
                              {
                                zone.placeholder
                              }
                            </div>

                            {zone.example ? (
                              <div className="anc-example">
                                {
                                  zone.example
                                }
                              </div>
                            ) : null}
                          </div>
                        )}

                      <textarea
                        ref={(
                          element
                        ) => {
                          textareasRef.current[
                            zone.id
                          ] =
                            element;
                        }}
                        className={`anc-textarea ${
                          isActive
                            ? "anc-textareaActive"
                            : ""
                        }`}
                        value={
                          zone.value
                        }
                        onChange={(
                          event
                        ) =>
                          handleChange(
                            zone.id,
                            event.target
                              .value
                          )
                        }
                        onFocus={() => {
                          openZone(
                            zone.id
                          );

                          setActiveZoneId(
                            zone.id
                          );
                        }}
                      />

                      {dictationZoneId ===
                        zone.id &&
                        (isDictationActive ||
                          interimTranscript ||
                          dictationError) && (
                          <div
                            className={`anc-dictationPanel ${
                              dictationError
                                ? "has-error"
                                : ""
                            }`}
                          >
                            {interimTranscript && (
                              <div className="anc-interimText">
                                {
                                  interimTranscript
                                }
                              </div>
                            )}

                            {dictationError && (
                              <div className="anc-dictationError">
                                {
                                  dictationError
                                }
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </div>
            );
          }
        )}
      </div>

      {/* =================================================
          ADD ZONE
      ================================================= */}

      <button
        type="button"
        className="anc-addZoneBtn"
        onClick={
          handleAddZone
        }
      >
        <span>+</span>

        {t(
          "addConsultation.actions.addTextZone",
          {
            defaultValue:
              "Ajouter une zone de texte",
          }
        )}
      </button>

      <AddMetricModal
        open={addVitalModalOpen}
        onClose={() => setAddVitalModalOpen(false)}
        onAdd={handleAddCustomVital}
      />

      {/* =================================================
          CORTI
      ================================================= */}

      <corti-dictation
        ref={
          dictationRef
        }
        aria-hidden="true"
        className="anc-hiddenDictation"
      />

      {/* =================================================
          CONSULTATION SAVE ERROR
      ================================================= */}

      {consultationSaveError && (
        <div
          className="anc-dictationError"
          role="alert"
          style={{ marginTop: 12 }}
        >
          {consultationSaveError}
        </div>
      )}

      {/* =================================================
          FOOTER
      ================================================= */}

      <div className="anc-footer">
        <button
          className="anc-cancelBtn"
          onClick={
            onCancel
          }
          type="button"
          disabled={
            savingVitals ||
            isSavingConsultation
          }
        >
          {t(
            "addConsultation.actions.cancelConsultation",
            {
              defaultValue:
                "Cancel",
            }
          )}
        </button>

        <div className="anc-footerActions">
          <button
            className="anc-primaryBtn"
            onClick={
              handleNext
            }
            type="button"
            disabled={
              loadingVitals ||
              savingVitals ||
              isSavingConsultation
            }
          >
            {savingVitals ||
            isSavingConsultation
              ? t(
                  "common.saving",
                  {
                    defaultValue:
                      "Saving...",
                  }
                )
              : t("addConsultation.actions.next")}
          </button>
        </div>
      </div>
    </section>
  );
}